import { Play, ExternalLink } from "lucide-react";
import { parseYoutubeUrl } from "../lib/youtube";

// First-video thumbnails for each playlist (used as the poster image)
const PLAYLIST_POSTERS = {
  "PL_8_gNnRI9chEgQGzHywqeM8AMTQwgHCu": "UX3Y-GsrD0g", // Abu Dhabi
  "PL_8_gNnRI9cg63Y8zU7KnkIovxKzIqOww": "3J-MIwADYdo", // China
  "PL_8_gNnRI9chWNR2-XxaSibu6g6RTX3vf": "h8c0uhJxo_w", // Dubai
  "PL_8_gNnRI9cgJcQuXbI7ycqyO3RVONdeF": "VI680X6CLNk", // MMT 2
  "PL_8_gNnRI9cjuRCjszVCLVWwYyysq37s2": "kcJ_-xtCcgg", // MMT 1
  "PL_8_gNnRI9chEBrAMLEpDEicrZC-T3tdk": "DeuyXWTOtFs", // MMT 3
  "PL_8_gNnRI9chQOR6_EvYjM-KQ1yRpG3TJ": "2s54iHU7wFc", // Saudi
};

// Clickable thumbnail that opens the YouTube playlist/video in a new tab.
export default function YoutubePlayer({ url, title, testId = "yt-player", posterOverride = null }) {
  const parsed = parseYoutubeUrl(url);
  // If the URL doesn't parse as a specific video/playlist (e.g. channel/playlists page)
  // but we do have a custom poster + link, still render a clickable thumbnail card.
  if (!parsed && !(posterOverride && url)) return null;

  const watchUrl = parsed ? parsed.watchUrl : url;
  const videoId = parsed ? parsed.videoId : null;
  const listId = parsed ? parsed.listId : null;

  // Pick a poster image: explicit override → custom image; else videoId → that thumbnail; else first video of the playlist
  const posterVideoId = videoId || PLAYLIST_POSTERS[listId];
  const poster = posterOverride || (posterVideoId ? `https://i.ytimg.com/vi/${posterVideoId}/maxresdefault.jpg` : null);

  return (
    <a
      href={watchUrl}
      target="_blank"
      rel="noreferrer"
      className="group relative block w-full bg-ink overflow-hidden"
      style={{ paddingTop: "56.25%" }}
      data-testid={`${testId}-thumb`}
      aria-label={title ? `Watch ${title} on YouTube` : "Watch on YouTube"}
    >
      {poster && (
        <img
          src={poster}
          alt=""
          className="absolute inset-0 w-full h-full object-cover img-hover"
          data-fallback="0"
          onLoad={(e) => {
            // YouTube serves a 120x90 grey placeholder when maxresdefault.jpg is missing.
            // Fall through to lower-res thumbs until we get a real image.
            const img = e.currentTarget;
            if (img.naturalWidth <= 120 && img.naturalHeight <= 90) {
              const tier = parseInt(img.dataset.fallback || "0", 10);
              const next = ["hqdefault.jpg", "mqdefault.jpg", "0.jpg"][tier];
              if (next && posterVideoId) {
                img.dataset.fallback = String(tier + 1);
                img.src = `https://i.ytimg.com/vi/${posterVideoId}/${next}`;
              }
            }
          }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors"/>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-bg">
        <div className="w-12 h-12 bg-ochre/95 flex items-center justify-center group-hover:bg-clay transition-colors shadow-md">
          <Play size={18} strokeWidth={2} fill="currentColor" className="translate-x-[1px]"/>
        </div>
        <div className="overline text-bg/80 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity text-[0.65rem]">
          {videoId ? "Watch on YouTube" : "Watch the playlist on YouTube"}
          <ExternalLink size={10}/>
        </div>
      </div>
    </a>
  );
}
